import { useLazyQuery, useQuery } from "@apollo/client/react";
import { PencilIcon } from "@heroicons/react/24/outline";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { Waiting } from "../../../../../animations";
import { ErrorFallback, Head } from "../../../../../components/core";
import { Button } from "../../../../../components/form";
import {
  GET_VEHICLE_BY_ID,
  Vehicle,
} from "../../../../../graphql/fleets/vehicles";
import {
  GET_SERVICEENTRY_BY_ID,
  VehicleServiceEntry,
} from "../../../../../graphql/fleets/vehicles/pages/service-entry";
import { AuthShield } from "../../../../auth/core";
import { ServiceEntryDetail } from "../../components/ServiceEntryDetail";

const ServiceEntryView = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
  let { serviceEntryId } = useParams();
  const { t } = useTranslation();

  const [fetchVehicle, { data: vehicleData }] = useLazyQuery<{
    fetchVehicle: Vehicle;
  }>(GET_VEHICLE_BY_ID);

  const { data, loading, error } = useQuery<{
    fetchServiceEntry: VehicleServiceEntry;
  }>(GET_SERVICEENTRY_BY_ID, {
    variables: { id: serviceEntryId },
    onCompleted(data) {
      fetchVehicle({ variables: { id: data?.fetchServiceEntry?.vehicle.id } });
    },
  });

  const serviceEntry = useMemo<VehicleServiceEntry | undefined>(
    () => data?.fetchServiceEntry,
    [data]
  );

  const vehicle = useMemo<Vehicle | undefined>(
    () => vehicleData?.fetchVehicle,
    [vehicleData]
  );

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <Head
        title={ServiceEntryViewResource.name}
        heading={ServiceEntryViewResource.name}
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: ServiceEntryViewResource.name,
            href: null,
          },
        ]}
      />
      <div className="max-w-medium mx-auto">
        <div className="rounded-xl bg-greyish p-5">
          <div className="mb-6 sm:flex sm:items-center md:mb-8">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-medium text-gray-900">
                {t("heading_vehicle_service_entry")}
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                {t("description_vehicle_service_entry")}
              </p>
            </div>
            <div className="ml-auto flex items-center space-x-2 pr-3">
              <AuthShield access={["read-vehicles"]}>
                <Button
                  type="link"
                  href={`/fleets/vehicles/service-entries/${serviceEntryId}/edit`}
                >
                  <PencilIcon className="mr-2 h-5 w-5" />
                  {t("text_edit")}
                </Button>
              </AuthShield>
            </div>
          </div>

          {loading || !serviceEntry ? (
            <Waiting />
          ) : (
            <ServiceEntryDetail
              serviceEntry={serviceEntry}
              vehicleBasic={
                vehicle
                  ? {
                      id: parseInt(vehicle.id),
                      name: vehicle.name,
                      vehicleImageUrl: vehicle.vehicleImageUrl,
                      make: vehicle.make,
                      model: vehicle.model,
                      year: vehicle.year,
                      operator: vehicle.operator,
                      status: vehicle.status,
                      fuelUnit: vehicle?.settings?.length
                        ? vehicle?.settings[0].fuelUnit
                        : null,
                      measurementUnit: vehicle?.settings?.length
                        ? vehicle?.settings[0].measurementUnit
                        : null,
                      primaryMeter: vehicle?.settings?.length
                        ? vehicle?.settings[0].primaryMeter
                        : null,
                    }
                  : null
              }
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ServiceEntryView;
export const ServiceEntryViewResource: ResourceProps = {
  name: "Service Entry",
  description: "View an existing service entry",
  access: ["read-vehicles"],
  path: "/service-entries/:serviceEntryId",
};
